"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import {
  Handshake,
  Bot,
  ArrowRight,
  Globe,
  TrendingUp,
  Package,
  Users,
  Headset,
  Percent,
  Search,
  ShoppingBag,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { cn } from "@/lib/utils";
import { productStats } from "@/lib/constants";

const products = [
  {
    key: "brand-partnerships",
    name: "Brand Partnerships",
    tagline: "Expand your catalog",
    description:
      "Source partner products, sell wholesale B2B, and launch a marketplace — all with zero inventory risk.",
    href: "/brand-partnerships",
    icon: Handshake,
    stats: [
      { icon: Globe, value: productStats.brandPartnerships.brands, label: "brands" },
      { icon: Users, value: productStats.brandPartnerships.partnerships, label: "partnerships" },
    ],
    network: [
      { icon: Search, label: "Source" },
      { icon: Handshake, label: "Partner" },
      { icon: ShoppingBag, label: "Sell" },
    ],
    features: ["Partner dropshipping", "Wholesale B2B", "Marketplace D2C"],
    theme: "light" as const,
  },
  {
    key: "buddy-ai",
    name: "Buddy AI",
    tagline: "Convert more visitors",
    description:
      "Three AI agents — Support, Sales, and Conversion — work together to turn your visitors into buyers, 24/7.",
    href: "/buddy-ai-assistant",
    icon: Bot,
    stats: [
      { icon: TrendingUp, value: `+${productStats.aiAssistant.conversionIncrease}`, label: "conversions" },
      { icon: Package, value: "24/7", label: "availability" },
    ],
    agents: [
      { icon: Headset, label: "Support" },
      { icon: TrendingUp, label: "Sales" },
      { icon: Percent, label: "Conversion" },
    ],
    features: ["AI Sales Agent", "AI Conversion Agent", "Lead capture"],
    theme: "dark" as const,
  },
];

const themeStyles = {
  light: {
    card: "bg-white border-stone-200 hover:border-primary-300 hover:shadow-primary-500/10",
    accent: "bg-primary-500",
    iconBg: "bg-primary-100 text-primary-600",
    statBg: "bg-primary-50",
    statText: "text-primary-700",
    featureBadge: "bg-white border-stone-200 text-stone-600",
    title: "text-stone-900",
    body: "text-stone-600",
    tagline: "text-stone-500",
    button: "bg-primary-600 text-white hover:bg-primary-700",
    networkLine: "bg-primary-200",
    networkNode: "bg-primary-50 border-primary-100 text-primary-600",
    networkLabel: "text-primary-700",
  },
  dark: {
    card: "bg-stone-900 border-stone-800 hover:border-stone-700 hover:shadow-stone-900/30",
    accent: "bg-green-500",
    iconBg: "bg-stone-800 text-green-400",
    statBg: "bg-stone-800",
    statText: "text-green-400",
    featureBadge: "bg-stone-800 border-stone-700 text-stone-300",
    title: "text-white",
    body: "text-stone-300",
    tagline: "text-stone-400",
    button: "bg-green-500 text-stone-950 hover:bg-green-400",
    networkLine: "bg-stone-700",
    networkNode: "bg-stone-800 border-stone-700 text-green-400",
    networkLabel: "text-stone-200",
  },
};

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const nodeVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
};

const lineVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

function AnimatedNetwork({
  items,
  theme,
}: {
  items: { icon: React.ElementType; label: string }[];
  theme: (typeof themeStyles)["light"];
}) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="flex items-center justify-center gap-1 py-4 mb-6"
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const isLast = index === items.length - 1;

        return (
          <div key={item.label} className="flex items-center">
            <motion.div
              variants={nodeVariants}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-xl border transition-transform duration-300 hover:scale-110 w-20 sm:w-[5.5rem] h-20 sm:h-[5.5rem]",
                theme.networkNode
              )}
            >
              <Icon className="w-5 h-5" />
              <span className={cn("text-[10px] font-semibold uppercase tracking-wider text-center leading-tight", theme.networkLabel)}>
                {item.label}
              </span>
            </motion.div>
            {!isLast && (
              <motion.div
                variants={lineVariants}
                style={{ originX: 0 }}
                className={cn("w-8 h-0.5 mx-1", theme.networkLine)}
              />
            )}
          </div>
        );
      })}
    </motion.div>
  );
}

export function ProductSelector() {
  return (
    <Section background="white" size="sm">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-display-1 text-stone-900 mb-4">
            Two ways to grow your store
          </h2>
          <p className="text-body-lg text-stone-600 max-w-3xl mx-auto">
            Choose the engine that fits your store right now. Most merchants use both.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {products.map((product, index) => {
            const theme = themeStyles[product.theme];
            const Icon = product.icon;

            return (
              <motion.div
                key={product.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  href={product.href}
                  className={cn(
                    "group relative block h-full rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden",
                    theme.card
                  )}
                >
                  {/* Top accent */}
                  <div className={cn("absolute top-0 left-0 right-0 h-1", theme.accent)} />

                  {/* Header: logo + title */}
                  <div className="flex flex-col sm:flex-row items-start gap-4 mb-5">
                    <div
                      className={cn(
                        "shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl transition-transform duration-300 group-hover:scale-110",
                        theme.iconBg
                      )}
                    >
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <span
                        className={cn(
                          "inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-1.5",
                          product.theme === 'dark'
                            ? 'bg-stone-800 text-stone-400'
                            : 'bg-stone-100 text-stone-500'
                        )}
                      >
                        {product.tagline}
                      </span>
                      <h3 className={cn("text-h2", theme.title)}>
                        {product.name}
                      </h3>
                    </div>
                  </div>

                  <p className={cn("text-body-md mb-5", theme.body)}>
                    {product.description}
                  </p>

                  {/* Animated visual */}
                  {product.network && (
                    <AnimatedNetwork items={product.network} theme={theme} />
                  )}
                  {product.agents && (
                    <AnimatedNetwork items={product.agents} theme={theme} />
                  )}

                  {/* Stats */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    {product.stats.map((stat) => (
                      <div
                        key={stat.label}
                        className={cn(
                          "flex items-center gap-2 px-3 py-1.5 rounded-lg",
                          theme.statBg
                        )}
                      >
                        <stat.icon className={cn("w-4 h-4", theme.statText)} />
                        <span className={cn("text-sm font-semibold", theme.statText)}>
                          {stat.value}
                        </span>
                        <span className={cn("text-xs", theme.body)}>
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {product.features.map((feature) => (
                      <span
                        key={feature}
                        className={cn(
                          "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border",
                          theme.featureBadge
                        )}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <span
                    className={cn(
                      "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors",
                      theme.button
                    )}
                  >
                    Learn more
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
